import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@ca/environment/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, retryWhen, take } from 'rxjs/operators';
import { AuthenticationService } from '.';
import { WorkflowType } from '../enums/workflow-type.enum';
import { MyQueueStatus, MyQueueStatusResult } from '../models/my-queue-status.model';
import { WorkflowRunStatusEvent } from '../models/workflow-run-status-event.model';
import { SignalrService } from './signalr.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class WorkflowRunQueueService {
    hasRunningTrainingWorkflow$: Observable<boolean>;
    hasRunningSegmentationWorkflow$: Observable<boolean>;
    hasRunningWorkflow$: Observable<boolean>;
    activeWorkflows$: Observable<MyQueueStatus[]>;
    workflowEvent$: Observable<WorkflowRunStatusEvent>;
    workflowCompleted$: Observable<WorkflowRunStatusEvent>;
    workflowSubmitted$: Observable<WorkflowRunStatusEvent>;

    private workflowEvent$$ = new Subject<WorkflowRunStatusEvent>();
    private readonly apiURL = '/api/workflow_run';
    private readonly headers = { 'Content-Type': 'application/json' };
    private readonly workflowCompletedTypes: string[] = ['CompletedSuccess', 'CompletedFailed', 'CompletedCancelled'];
    private activeWorkflows$$ = new BehaviorSubject<MyQueueStatus[]>([]);

    get activeWorkflows(): MyQueueStatus[] {
        return this.activeWorkflows$$.getValue();
    }

    constructor(
        protected http: HttpClient,
        private signalRService: SignalrService,
        private authenticationService: AuthenticationService,
    ) {
        this.workflowEvent$ = this.workflowEvent$$.asObservable();
        this.workflowCompleted$ = this.workflowEvent$.pipe(
            filter(({ stateDescription }: WorkflowRunStatusEvent) => this.workflowCompletedTypes.includes(stateDescription)),
        );
        this.workflowSubmitted$ = this.workflowEvent$.pipe(filter(({ stateDescription }: WorkflowRunStatusEvent) => stateDescription === 'Submitted'));

        this.activeWorkflows$ = this.activeWorkflows$$.asObservable();

        this.hasRunningTrainingWorkflow$ = this.activeWorkflows$$.asObservable().pipe(
            map((queueStatuses: MyQueueStatus[]) => queueStatuses.filter((q: MyQueueStatus) => this.isTrainingWorkflow(q.workflowType)).length > 0),
            distinctUntilChanged(),
        );

        this.hasRunningSegmentationWorkflow$ = this.activeWorkflows$$.asObservable().pipe(
            map((queueStatuses: MyQueueStatus[]) => queueStatuses.filter((q: MyQueueStatus) => q.workflowType === WorkflowType.SEGMENTATION).length > 0),
            distinctUntilChanged(),
        );

        this.hasRunningWorkflow$ = this.activeWorkflows$$.asObservable().pipe(map((queueStatuses: MyQueueStatus[]) => queueStatuses.length > 0));
    }

    establishWebsocketConnection(): void {
        this.updateMyQueue();

        const callback = (res: WorkflowRunStatusEvent) => {
            if (res != null) {
                this.workflowEvent$$.next(res);
            }
            this.updateMyQueue();
        };
        this.signalRService.startConnection(environment.websocketOrigin + '/workflowruns', 'WorkflowRunStatusEvent', callback);
    }

    hasActiveStepByStepWorkflow(): boolean {
        return this.activeWorkflows$$.getValue() && this.activeWorkflows$$.getValue().filter(q => q.isInteractiveWorkflow).length > 0;
    }

    /**
     * @deprecated use hasRunningTrainingWorkflow$
     */
    hasRunningTrainingWorkflow(): boolean {
        return this.activeWorkflows$$.getValue() && this.activeWorkflows$$.getValue().filter(q => this.isTrainingWorkflow(q.workflowType)).length > 0;
    }

    /**
     * @deprecated use hasRunningSegmentationWorkflow$
     */
    hasRunningSegmentationWorkflow(): boolean {
        return this.activeWorkflows$$.getValue() && this.activeWorkflows$$.getValue().filter(q => q.workflowType === WorkflowType.SEGMENTATION).length > 0;
    }

    private getMyQueueStatus$(): Observable<MyQueueStatusResult> {
        return this.http.get<MyQueueStatusResult>(`${this.apiURL}/queuestatus/my`, { headers: this.headers });
    }

    private isTrainingWorkflow(workflowType: WorkflowType): boolean {
        return [WorkflowType.SEGMENTATION_TRAINING, WorkflowType.SEGMENTATION_CONTINUE_TRAINING].indexOf(workflowType) > -1;
    }

    private updateMyQueue(): void {
        // the if condition prevents a 401 error that happens when the request gets sent after the user is already logged out
        // because of the setTimeout behavior.
        // This also happens frequently during the cypress tests, because in cypress the localStorage is cleared after every test
        if (this.authenticationService.hasValidAccessToken()) {
            this.getMyQueueStatus$()
                .pipe(
                    retryWhen(error$ => error$.pipe(delay(2000), take(10), untilDestroyed(this))),
                    map((response: MyQueueStatusResult) => {
                        if (this.authenticationService.hasValidAccessToken()) {
                            this.activeWorkflows$$.next(response.data);
                        }
                    }),
                    untilDestroyed(this),
                )
                .subscribe();
        }
    }
}
